import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Mail, ArrowLeft, CheckCircle } from 'lucide-react';
import { useUserStore } from '@/lib/user-store';
import { useTranslation } from '@/hooks/useTranslation';
import toast from 'react-hot-toast';

const ForgotPasswordPage: React.FC = () => {
  const { t } = useTranslation();
  const requestPasswordReset = useUserStore((s) => s.requestPasswordReset);

  const [email, setEmail] = useState('');
  const [sent, setSent] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    if (!email.trim() || loading) return;

    setLoading(true);
    const result = await requestPasswordReset(email);
    setLoading(false);

    if (result === true) {
      setSent(true);
      return;
    }
    if (result === 'NOT_REGISTERED') {
      toast.error(t.auth.noAccountFound || 'This email is not registered. Please check the address or sign up first.');
      return;
    }
    if (result === 'RATE_LIMITED') {
      toast.error('Too many requests. Please wait a few minutes and try again.');
      return;
    }
    toast.error('Could not send reset email. Try again.');
  };

  if (sent) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4 pt-20 pb-10">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
          className="w-full max-w-md text-center"
        >
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ delay: 0.2, type: 'spring', stiffness: 200, damping: 15 }}
            className="mx-auto w-16 h-16 rounded-full bg-green-50 dark:bg-green-500/10 flex items-center justify-center mb-6"
          >
            <CheckCircle className="text-green-500" size={32} />
          </motion.div>

          <h1 className="text-2xl font-syncopate font-bold uppercase tracking-tight mb-3">
            {t.auth.resetLinkSent}
          </h1>
          <p className="text-sm text-black/50 dark:text-white/50 mb-8 leading-relaxed">
            {t.auth.checkEmail}
          </p>

          <Link
            to="/login"
            className="inline-flex items-center gap-2 text-[10px] font-bold uppercase tracking-[0.2em] text-black/40 dark:text-white/40 hover:text-black dark:hover:text-white transition-colors"
          >
            <ArrowLeft size={14} />
            {t.auth.backToLogin}
          </Link>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center px-4 pt-20 pb-10">
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
        className="w-full max-w-md"
      >
        <div className="text-center mb-8">
          <div className="mx-auto w-14 h-14 rounded-full bg-black/[0.04] dark:bg-white/[0.06] flex items-center justify-center mb-5">
            <Mail className="text-black/40 dark:text-white/40" size={24} />
          </div>
          <h1 className="text-2xl font-syncopate font-bold uppercase tracking-tight">
            {t.auth.forgotPasswordTitle}
          </h1>
          <p className="mt-3 text-sm text-black/40 dark:text-white/40 leading-relaxed">
            {t.auth.forgotPasswordDesc}
          </p>
        </div>

        <form onSubmit={handleSubmit} className="bg-white dark:bg-black border border-black/[0.04] dark:border-white/[0.04] rounded-2xl p-6 sm:p-8 space-y-5">
          <div>
            <label className="block text-[9px] font-bold uppercase tracking-[0.2em] text-black/40 dark:text-white/40 mb-2">
              {t.auth.email}
            </label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full px-4 py-3.5 bg-black/[0.03] dark:bg-white/[0.05] border border-black/[0.06] dark:border-white/[0.08] rounded-xl text-sm font-medium focus:outline-none focus:border-black/20 dark:focus:border-white/20 transition-all placeholder:text-black/20 dark:placeholder:text-white/20"
              placeholder="email@example.com"
              required
            />
          </div>

          <button
            type="submit"
            disabled={loading}
            className="w-full flex items-center justify-center gap-2 py-4 bg-black dark:bg-white text-white dark:text-black text-[10px] font-bold uppercase tracking-[0.3em] rounded-xl hover:opacity-90 active:scale-[0.98] transition-all disabled:opacity-50"
          >
            {loading ? (
              <div className="w-4 h-4 border-2 border-white/30 dark:border-black/30 border-t-white dark:border-t-black rounded-full animate-spin" />
            ) : (
              <Mail size={14} />
            )}
            {t.auth.resetPasswordButton}
          </button>

          <p className="text-center">
            <Link
              to="/login"
              className="inline-flex items-center gap-2 text-[10px] font-bold uppercase tracking-[0.15em] text-black/30 dark:text-white/30 hover:text-black dark:hover:text-white transition-colors"
            >
              <ArrowLeft size={12} />
              {t.auth.backToLogin}
            </Link>
          </p>
        </form>
      </motion.div>
    </div>
  );
};

export default ForgotPasswordPage;
